import React, { useEffect, useState, useRef } from "react";

/**
 * Sleek architectural crosshair cursor that inverts against its background.
 * Uses `mix-blend-mode: difference` so the cursor renders as the inverse of
 * whatever pixels sit beneath it — dark on light, light on dark, automatically.
 * Interactive hover keeps its own visual (marine ring, no invert) so it stays
 * a positive-space affordance.
 */
export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const hoverRingRef = useRef(null);
  const rafRef = useRef(0);
  const visibleRef = useRef(false);
  const [hover, setHover] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(pointer: coarse)").matches) return;

    let x = 0, y = 0, rx = 0, ry = 0;
    const move = (e) => {
      x = e.clientX; y = e.clientY;
      if (!visibleRef.current) { visibleRef.current = true; setVisible(true); }
      const t = e.target;
      const isInteractive = !!(t && (t.closest && t.closest('a, button, [role="button"], input, textarea, select, [data-cursor="hover"]')));
      setHover(isInteractive);
    };
    const leave = () => { visibleRef.current = false; setVisible(false); };
    const raf = () => {
      rx += (x - rx) * 0.22;
      ry += (y - ry) * 0.22;
      if (dotRef.current) dotRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      if (ringRef.current) ringRef.current.style.transform = `translate3d(${rx}px, ${ry}px, 0)`;
      if (hoverRingRef.current) hoverRingRef.current.style.transform = `translate3d(${rx}px, ${ry}px, 0)`;
      rafRef.current = requestAnimationFrame(raf);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseleave", leave);
    rafRef.current = requestAnimationFrame(raf);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseleave", leave);
      cancelAnimationFrame(rafRef.current);
    };
    // Empty deps: the raf loop reads position via closures + refs; keeping
    // `visible` out avoids re-subscribing every time it flips and leaking
    // extra requestAnimationFrame loops. (Code review 2026-02-25 LOW)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches) return null;

  return (
    <>
      {/* Crosshair dot — inverts against background via mix-blend-mode: difference */}
      <div
        ref={dotRef}
        aria-hidden
        style={{
          position: "fixed", left: 0, top: 0, zIndex: 9999,
          pointerEvents: "none",
          width: 0, height: 0,
          opacity: visible && !hover ? 1 : 0,
          transition: "opacity 200ms ease",
          mixBlendMode: "difference",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" style={{ transform: "translate(-9px, -9px)" }}>
          <line x1="0" y1="9" x2="6" y2="9" stroke="#FFFFFF" strokeWidth="1" />
          <line x1="12" y1="9" x2="18" y2="9" stroke="#FFFFFF" strokeWidth="1" />
          <line x1="9" y1="0" x2="9" y2="6" stroke="#FFFFFF" strokeWidth="1" />
          <line x1="9" y1="12" x2="9" y2="18" stroke="#FFFFFF" strokeWidth="1" />
        </svg>
      </div>

      {/* Idle trailing ring — inverts against background via mix-blend-mode: difference */}
      <div
        ref={ringRef}
        aria-hidden
        style={{
          position: "fixed", left: 0, top: 0, zIndex: 9998,
          pointerEvents: "none",
          opacity: visible && !hover ? 1 : 0,
          transition: "opacity 200ms ease",
          mixBlendMode: "difference",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            transform: "translate(-14px, -14px)",
            border: "1px solid #FFFFFF",
            borderRadius: "9999px",
            backgroundColor: "transparent",
          }}
        />
      </div>

      {/* Hover ring — solid marine positive-space affordance (no invert) */}
      <div
        ref={hoverRingRef}
        aria-hidden
        style={{
          position: "fixed", left: 0, top: 0, zIndex: 9998,
          pointerEvents: "none",
          opacity: visible && hover ? 1 : 0,
          transition: "opacity 250ms cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            transform: "translate(-28px, -28px)",
            border: "1px solid #16403F",
            borderRadius: "9999px",
            backgroundColor: "rgba(22, 64, 63, 0.08)",
            transition: "transform 350ms cubic-bezier(0.16,1,0.3,1)",
          }}
        />
      </div>
    </>
  );
}
